import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export const SEARCH_LURE_FREE_REGEN_CAP = 20;
export const SEARCH_LURE_REGEN_INTERVAL_MS = 3 * 60 * 60 * 1000;
export const SEARCH_LURE_REGEN_INTERVAL_HOURS = 3;

export type SearchLureBundleId = 'scout_5' | 'explorer_15' | 'expedition_30';

export type SearchLureBundle = {
  id: SearchLureBundleId;
  name: string;
  quantity: number;
  costShells: number;
  description: string;
};

export const SEARCH_LURE_BUNDLES: SearchLureBundle[] = [
  { id: 'scout_5', name: 'Scout Pack', quantity: 5, costShells: 25, description: 'Five extra area searches.' },
  { id: 'explorer_15', name: 'Explorer Pack', quantity: 15, costShells: 60, description: 'A better-value refill for active scouting.' },
  { id: 'expedition_30', name: 'Expedition Pack', quantity: 30, costShells: 100, description: 'Thirty searches for longer planning sessions.' },
];

export type ConsumeSearchLureResult = {
  ok: boolean;
  charged: boolean;
  reusedArea?: boolean;
  searchLureBalance: number;
  passiveSearchLuresGranted?: number;
  searchLureRegenAnchorAtMs?: number;
  nextFreeSearchLureAtMs?: number | null;
  freeRegenCap?: number;
  areaKey?: string;
  requestId: string;
};

export type PurchaseSearchLureBundleResult = {
  ok: boolean;
  bundleId: SearchLureBundleId;
  quantity: number;
  spentShells: number;
  shellBalance: number;
  searchLureBalance: number;
  passiveSearchLuresGranted?: number;
  searchLureRegenAnchorAtMs?: number;
  nextFreeSearchLureAtMs?: number | null;
  freeRegenCap?: number;
  requestId: string;
};

export type SearchLureWalletPreview = {
  balance: number;
  storedBalance: number;
  passiveGranted: number;
  freeRegenCap: number;
  regenIntervalHours: number;
  nextFreeSearchLureAtMs: number | null;
  isAtOrAboveFreeCap: boolean;
};

export function formatSearchLureRefillCountdown(
  nextFreeSearchLureAtMs: number | null | undefined,
  nowMs = Date.now(),
): string {
  const target = Number(nextFreeSearchLureAtMs || 0);
  if (!Number.isFinite(target) || target <= 0) return '';

  const remainingMs = Math.max(0, target - nowMs);
  if (remainingMs <= 30_000) return 'soon';

  const totalMinutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

export function getSearchLureRefillLabel(
  wallet: SearchLureWalletPreview,
  nowMs = Date.now(),
): string {
  if (wallet.isAtOrAboveFreeCap) {
    return `Free refill paused at ${wallet.freeRegenCap}`;
  }

  const countdown = formatSearchLureRefillCountdown(
    wallet.nextFreeSearchLureAtMs,
    nowMs,
  );

  return countdown
    ? `+1 free lure in ${countdown}`
    : `+1 every ${wallet.regenIntervalHours}h`;
}

export function getSearchLureWalletPreview(profile: any, nowMs = Date.now()): SearchLureWalletPreview {
  const storedBalance = Math.max(0, Math.floor(Number(profile?.searchLures || 0) || 0));
  const anchorRaw = Number(profile?.searchLureRegenAnchorAtMs || profile?.lastSearchLureAreaAtMs || 0);
  const anchor = Number.isFinite(anchorRaw) && anchorRaw > 0 ? anchorRaw : nowMs;

  if (storedBalance >= SEARCH_LURE_FREE_REGEN_CAP) {
    return {
      balance: storedBalance,
      storedBalance,
      passiveGranted: 0,
      freeRegenCap: SEARCH_LURE_FREE_REGEN_CAP,
      regenIntervalHours: SEARCH_LURE_REGEN_INTERVAL_HOURS,
      nextFreeSearchLureAtMs: null,
      isAtOrAboveFreeCap: true,
    };
  }

  const elapsed = Math.max(0, nowMs - anchor);
  const accrued = Math.max(0, Math.floor(elapsed / SEARCH_LURE_REGEN_INTERVAL_MS));
  const passiveGranted = Math.min(accrued, SEARCH_LURE_FREE_REGEN_CAP - storedBalance);
  const balance = storedBalance + passiveGranted;
  const nextAnchor = anchor + passiveGranted * SEARCH_LURE_REGEN_INTERVAL_MS;
  const isAtOrAboveFreeCap = balance >= SEARCH_LURE_FREE_REGEN_CAP;

  return {
    balance,
    storedBalance,
    passiveGranted,
    freeRegenCap: SEARCH_LURE_FREE_REGEN_CAP,
    regenIntervalHours: SEARCH_LURE_REGEN_INTERVAL_HOURS,
    nextFreeSearchLureAtMs: isAtOrAboveFreeCap ? null : nextAnchor + SEARCH_LURE_REGEN_INTERVAL_MS,
    isAtOrAboveFreeCap,
  };
}


export function createSearchLureRequestId(prefix = 'search'): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {}
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

const normalizeSearchLureLongitude = (value: unknown): number => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return Number.NaN;
  const wrapped = ((((raw + 180) % 360) + 360) % 360) - 180;
  return Object.is(wrapped, -0) ? 0 : wrapped;
};

export async function consumeSearchLure(input: {
  lat: number;
  lng?: number;
  lon?: number;
  requestId?: string;
}): Promise<ConsumeSearchLureResult> {
  const lat = Number(input.lat);
  const lng = normalizeSearchLureLongitude(input.lng ?? input.lon);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90
  ) {
    throw new Error('Invalid Search Lure coordinates.');
  }

  const requestId = String(
    input.requestId || createSearchLureRequestId('area'),
  ).trim();

  const call = httpsCallable<
    { lat: number; lng: number; requestId: string },
    ConsumeSearchLureResult
  >(functions, 'consumeSearchLure');

  const result = await call({ lat, lng, requestId });
  return result.data;
}

export async function purchaseSearchLureBundle(
  bundleId: SearchLureBundleId,
  requestId = createSearchLureRequestId('lurepack'),
): Promise<PurchaseSearchLureBundleResult> {
  const call = httpsCallable<
    { bundleId: SearchLureBundleId; requestId: string },
    PurchaseSearchLureBundleResult
  >(functions, 'purchaseSearchLureBundle');
  const result = await call({ bundleId, requestId });
  return result.data;
}


export type RewardedSearchLureEligibilityResult = {
  ok: boolean;
  eligible: boolean;
  usedToday: number;
  remainingToday: number;
  dailyCap: number;
  rewardAmount: number;
};

export type RewardedSearchLureStatusResult = {
  ok: boolean;
  status: 'pending' | 'granted' | 'capped' | 'duplicate-request' | string;
  requestId: string;
  transactionId?: string;
  searchLureBalance?: number;
  grantedAmount: number;
  dailyCount?: number;
  dailyCap: number;
};

export async function getRewardedSearchLureEligibility(): Promise<RewardedSearchLureEligibilityResult> {
  const call = httpsCallable<Record<string, never>, RewardedSearchLureEligibilityResult>(
    functions,
    'getRewardedSearchLureEligibility',
  );
  const result = await call({});
  return result.data;
}

export async function getRewardedSearchLureStatus(
  requestId: string,
): Promise<RewardedSearchLureStatusResult> {
  const call = httpsCallable<{ requestId: string }, RewardedSearchLureStatusResult>(
    functions,
    'getRewardedSearchLureStatus',
  );
  const result = await call({ requestId });
  return result.data;
}
